import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Avatar,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { Review } from "shared/entities";

interface ReviewsSectionProps {
  reviews: Review[];
}

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <Box sx={{ display: "flex", gap: 0.25 }}>
    {[1, 2, 3, 4, 5].map((star) =>
      star <= rating ? (
        <StarIcon key={star} sx={{ color: "#FFC107", fontSize: 18 }} />
      ) : (
        <StarBorderIcon key={star} sx={{ color: "#FFC107", fontSize: 18 }} />
      ),
    )}
  </Box>
);

const INITIAL_COUNT = 3;

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ reviews }) => {
  const [showAll, setShowAll] = useState(false);
  const displayedReviews = showAll ? reviews : reviews.slice(0, INITIAL_COUNT);

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Reviews from Sitters
      </Typography>

      {reviews.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <RateReviewIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary">
            No reviews yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete your first booking to receive reviews!
          </Typography>
        </Box>
      ) : (
        <>
          <Stack divider={<Divider />} spacing={0}>
            {displayedReviews.map((review) => (
              <Box key={review.id} sx={{ py: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Avatar
                    src={review.sitterPhotoUrl || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: "#178B8E",
                      fontSize: "0.9rem",
                    }}
                  >
                    {review.sitterName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {review.sitterName}
                    </Typography>
                    <StarRating rating={review.rating} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ mb: 0.5 }}
                >
                  {review.text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(review.date)}
                </Typography>
              </Box>
            ))}
          </Stack>

          {reviews.length > INITIAL_COUNT && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                onClick={() => setShowAll((prev) => !prev)}
                variant="text"
                sx={{ textTransform: "none", color: "#178B8E" }}
              >
                {showAll ? "Show Less" : `View All ${reviews.length} Reviews`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default ReviewsSection;
